import React, { useContext, useEffect, useRef } from 'react';
import { SurveyDefinition, SurveyStepDefinition, VideoInstructionStepDefinition } from '../../../types/Survey';
import { StepResultModel } from '../../../types/SurveyResult';
import { getNextButtonStyles, getTextStyles } from '../Util/surveyStyles';
import { buildStepResult, resolvePresentedDate } from '../Util/surveyStepUtils';
import { SurveyStepContext } from '../Util/surveyStepContext';
import SurveyMarkdown from '../Components/SurveyMarkdown/SurveyMarkdown';
import SurveyNextButton from '../Components/SurveyNextButton/SurveyNextButton';

export interface VideoInstructionStepProps {
    step: VideoInstructionStepDefinition;
    surveyDefinition: SurveyDefinition;
    nextStep: SurveyStepDefinition | null;
    onContinue: (stepResult: StepResultModel) => void;
    presentedDate?: string;
}

const VIDEO_FRAME_TEMPLATE =
    '<div style="height: 100%; width: 100%; display: -webkit-flexbox; display: -ms-flexbox; display: -webkit-flex; display: flex; -webkit-flex-align: center; -ms-flex-align: center; -webkit-align-items: center; align-items: center; justify-content: center;"><video style="max-width: 100%; max-height: 300px;" controls poster="{thumbnailURL}" src="{videoURL}">Your browser does not support the video tag.</video></div>';

export default function VideoInstructionStep({
    step,
    surveyDefinition,
    nextStep,
    onContinue,
    presentedDate: presentedDateProp,
}: VideoInstructionStepProps) {
    const { webViewStepFrameUrl } = useContext(SurveyStepContext) ?? {};
    const presentedDate = resolvePresentedDate(presentedDateProp);
    const last = nextStep == null;

    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    // Content is posted in via message passing, not assigned directly: IE blocks dynamically
    // setting sandboxed iframe content any other way.
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe || !webViewStepFrameUrl) return;

        const webContent = VIDEO_FRAME_TEMPLATE
            .replace('{videoURL}', step.videoURL ?? '')
            .replace('{thumbnailURL}', step.image ?? '');

        iframe.onload = () => {
            iframe.contentWindow?.postMessage({ name: 'RKStudioStepContent', content: webContent }, webViewStepFrameUrl);
        };
        iframe.src = webViewStepFrameUrl;
    }, [step.videoURL, step.image, webViewStepFrameUrl]);

    function handleContinue() {
        onContinue(buildStepResult(step.identifier ?? '', presentedDate, []));
    }

    return (
        <>
            <div className="current-step video-instruction-step">
                <div className="step-content">
                    <div className="instruction-step-text">
                        {step.title && (
                            <div className="step-title" style={getTextStyles('title', surveyDefinition, step, true)}>
                                <SurveyMarkdown text={step.title} inline={true} />
                            </div>
                        )}
                        {step.text && (
                            <div className="step-text" style={getTextStyles('text', surveyDefinition, step, true)}>
                                <SurveyMarkdown text={step.text} />
                            </div>
                        )}
                        {step.detailText && (
                            <div className="step-text" style={getTextStyles('detailText', surveyDefinition, step, true)}>
                                <SurveyMarkdown text={step.detailText} />
                            </div>
                        )}
                    </div>
                    <div className="viewport">
                        <iframe
                            ref={iframeRef}
                            allowFullScreen
                            scrolling="no"
                            sandbox="allow-scripts allow-same-origin"
                            src=""
                            style={{ height: '100%', border: 'none', padding: 0, margin: 0, width: '100%', display: 'inline-block', overflow: 'hidden' }}
                        />
                    </div>
                    <SurveyNextButton
                        nextButtonText={step.nextButtonText}
                        isLastStep={last}
                        style={getNextButtonStyles(surveyDefinition, step, true, true) as React.CSSProperties}
                        onClick={handleContinue}
                    />
                </div>
            </div>
            {step.footnote && (
                <div className="step-footnote" style={getTextStyles('footnote', surveyDefinition, step, true)}>
                    <SurveyMarkdown text={step.footnote} inline={true} />
                </div>
            )}
        </>
    );
}
